import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  role?: 'admin' | 'user'; // FIXED: Removed 'partner' - partners use PartnerAuthContext
  nationality?: string;
  country?: string;
  gender?: string;
  city?: string;
  date_of_birth?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);
  const [sessionCheckInProgress, setSessionCheckInProgress] = useState(false);

  // Separate useEffect to fetch profile when user changes
  useEffect(() => {
    if (user && initialAuthCheckComplete) {
      fetchUserProfile(user.id);
    } else if (!user) {
      setUserProfile(null);
    }
  }, [user, initialAuthCheckComplete]);

  useEffect(() => {
    let isMounted = true;

    // Get initial session with error handling
    const initializeAuth = async () => {
      try {
        setSessionCheckInProgress(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session initialization error:', error);
          // Only sign out if it's a real session error, not a "no session" state
          if (error.message !== 'Auth session missing!' && error.message !== 'No session') {
            await supabase.auth.signOut();
          }
          if (isMounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            setInitialAuthCheckComplete(true);
            setSessionCheckInProgress(false);
          }
          return;
        }

        if (isMounted) {
          setUser(session?.user ?? null);
          setLoading(false);
          setInitialAuthCheckComplete(true);
          setSessionCheckInProgress(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          setInitialAuthCheckComplete(true);
          setSessionCheckInProgress(false);
        }
      }
    };

    initializeAuth();

    // CRITICAL FIX: Only respond to meaningful auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {
        // Ignore INITIAL_SESSION to prevent reload loops
        if (event === 'INITIAL_SESSION') {
          return;
        }

        // CRITICAL FIX: Ignore session check events during initial auth check
        if (sessionCheckInProgress) {
          console.log('Ignoring auth event during session check:', event);
          return;
        }

        console.log('Auth event:', event);

        if (!isMounted) return;

        // Only update state for actual auth changes
        if (event === 'SIGNED_OUT') {
          // CRITICAL FIX: Only clear state if this is an intentional sign-out
          // Check if we actually have a user to sign out
          if (user) {
            console.log('Processing sign-out for existing user');
            setUser(null);
            setUserProfile(null);
            setLoading(false);
          } else {
            console.log('Ignoring SIGNED_OUT event - no user was signed in');
          }
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Only update if user actually changed or if we don't have a user yet
          if (session?.user && (!user || session.user.id !== user.id)) {
            console.log('Updating user from auth event:', event);
            setUser(session.user);
            setLoading(false);
          } else if (session?.user && user && session.user.id === user.id) {
            // Session refresh for same user - don't trigger state update
            console.log('Session refreshed for existing user');
          }
        } else if (event === 'USER_UPDATED') {
          if (session?.user) {
            setUser(session.user);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []); // Empty deps - runs once on mount

  const fetchUserProfile = async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      if (data) {
        setUserProfile({
          id: data.id,
          email: data.email,
          full_name: data.full_name,
          avatar_url: data.avatar_url,
          role: data.role,
          nationality: data.nationality,
          country: data.country,
          gender: data.gender,
          city: data.city,
          date_of_birth: data.date_of_birth,
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    setSessionCheckInProgress(true);
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // CRITICAL FIX: Wait a moment before allowing auth state changes
    // This prevents premature SIGNED_OUT events from interrupting login
    setTimeout(() => {
      setSessionCheckInProgress(false);
    }, 1000);
    
    return { error };
  };

  const signUp = async (email: string, password: string, userData?: any) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
      },
    });
    return { error };
  };

  const signOut = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear state even if signOut fails
      setUser(null);
      setUserProfile(null);
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) return { error: new Error('No user logged in') };

    const { error } = await supabase
      .from('user_profiles')
      .update(data)
      .eq('id', user.id);

    if (!error) {
      setUserProfile(prev => prev ? { ...prev, ...data } : null);
    }

    return { error };
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};